// redux/slices/reportExportConsentSlice.js
import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";

import { resetStore } from "./resetStore";
import { clearJwtToken } from "../../Utils/authStorage";
import { apiRequest } from "../../Utils/apiClient";

export const CURRENT_REPORT_EXPORT_DISCLAIMER_VERSION = "2026-08-06-v1";

const getError = (error, fallback) =>
  error?.response?.data ||
  error?.message ||
  fallback;

const unwrapResponse = (response) => response?.data || response || {};

const clearAuthOnUnauthorized = async (error) => {
  const status = error?.response?.status;
  if (status !== 401 && status !== 403) return;

  try {
    await clearJwtToken();
  } catch {}
};

const requireUid = (uid) => {
  if (!uid) throw new Error("UID не передан");
  return String(uid);
};

const EMPTY_CONSENT_STATE = Object.freeze({
  accepted: false,
  requiredVersion: CURRENT_REPORT_EXPORT_DISCLAIMER_VERSION,
  consent: null,
  status: "idle",
  acceptStatus: "idle",
  error: null,
});

const createEmptyConsentState = () => ({ ...EMPTY_CONSENT_STATE });

export const fetchReportExportConsent = createAsyncThunk(
  "reportExportConsent/fetchStatus",
  async ({ uid }, { rejectWithValue }) => {
    let normalizedUid = "";

    try {
      normalizedUid = requireUid(uid);

      const response = await apiRequest(
        "GET",
        `/report-export-consents/${encodeURIComponent(normalizedUid)}/status`
      );
      const data = unwrapResponse(response);

      return {
        uid: normalizedUid,
        accepted: data?.accepted === true,
        requiredVersion:
          data?.requiredVersion || CURRENT_REPORT_EXPORT_DISCLAIMER_VERSION,
        consent: data?.consent || null,
      };
    } catch (error) {
      await clearAuthOnUnauthorized(error);

      return rejectWithValue({
        uid: normalizedUid || String(uid || ""),
        error: getError(error, "fetchReportExportConsent failed"),
        status: error?.response?.status || null,
      });
    }
  }
);

export const acceptReportExportConsent = createAsyncThunk(
  "reportExportConsent/accept",
  async (
    {
      uid,
      language,
      deviceModel = null,
      version = CURRENT_REPORT_EXPORT_DISCLAIMER_VERSION,
    },
    { rejectWithValue }
  ) => {
    let normalizedUid = "";

    try {
      normalizedUid = requireUid(uid);

      const response = await apiRequest(
        "POST",
        `/report-export-consents/${encodeURIComponent(normalizedUid)}/accept`,
        {
          version,
          language,
          deviceModel,
        }
      );
      const data = unwrapResponse(response);

      return {
        uid: normalizedUid,
        accepted: data?.accepted === true,
        requiredVersion:
          data?.requiredVersion || CURRENT_REPORT_EXPORT_DISCLAIMER_VERSION,
        consent: data?.consent || null,
      };
    } catch (error) {
      await clearAuthOnUnauthorized(error);

      return rejectWithValue({
        uid: normalizedUid || String(uid || ""),
        error: getError(error, "acceptReportExportConsent failed"),
        status: error?.response?.status || null,
        requiredVersion: error?.response?.data?.requiredVersion || null,
      });
    }
  }
);

const initialState = {
  byUid: {},
};

const ensureUidState = (state, uid) => {
  if (!state.byUid[uid]) {
    state.byUid[uid] = createEmptyConsentState();
  }
  return state.byUid[uid];
};

const reportExportConsentSlice = createSlice({
  name: "reportExportConsent",
  initialState,
  reducers: {
    clearReportExportConsentError: (state, action) => {
      const uid = String(action.payload || "");
      if (uid && state.byUid[uid]) {
        state.byUid[uid].error = null;
      }
    },
    clearReportExportConsentForUid: (state, action) => {
      const uid = String(action.payload || "");
      if (uid) delete state.byUid[uid];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReportExportConsent.pending, (state, action) => {
        const uid = String(action.meta.arg?.uid || "");
        if (!uid) return;
        const item = ensureUidState(state, uid);
        item.status = "loading";
        item.error = null;
      })
      .addCase(fetchReportExportConsent.fulfilled, (state, action) => {
        const { uid, accepted, requiredVersion, consent } = action.payload;
        const item = ensureUidState(state, uid);
        item.status = "succeeded";
        item.accepted = accepted;
        item.requiredVersion = requiredVersion;
        item.consent = consent;
        item.error = null;
      })
      .addCase(fetchReportExportConsent.rejected, (state, action) => {
        const uid = String(action.payload?.uid || action.meta.arg?.uid || "");
        if (!uid) return;
        const item = ensureUidState(state, uid);
        item.status = "failed";
        item.error = action.payload?.error || action.error?.message || "Request failed";
      })
      .addCase(acceptReportExportConsent.pending, (state, action) => {
        const uid = String(action.meta.arg?.uid || "");
        if (!uid) return;
        const item = ensureUidState(state, uid);
        item.acceptStatus = "loading";
        item.error = null;
      })
      .addCase(acceptReportExportConsent.fulfilled, (state, action) => {
        const { uid, accepted, requiredVersion, consent } = action.payload;
        const item = ensureUidState(state, uid);
        item.status = "succeeded";
        item.acceptStatus = "succeeded";
        item.accepted = accepted;
        item.requiredVersion = requiredVersion;
        item.consent = consent;
        item.error = null;
      })
      .addCase(acceptReportExportConsent.rejected, (state, action) => {
        const uid = String(action.payload?.uid || action.meta.arg?.uid || "");
        if (!uid) return;
        const item = ensureUidState(state, uid);
        item.acceptStatus = "failed";
        item.error = action.payload?.error || action.error?.message || "Request failed";
      })
      .addCase(resetStore, (state) => {
        state.byUid = {};
      });
  },
});

export const selectReportExportConsentByUid = (state, uid) =>
  state.reportExportConsent?.byUid?.[String(uid)] || EMPTY_CONSENT_STATE;

export const selectHasCurrentReportExportConsent = createSelector(
  [selectReportExportConsentByUid],
  (item) =>
    item.accepted === true &&
    item.requiredVersion === CURRENT_REPORT_EXPORT_DISCLAIMER_VERSION
);

export const {
  clearReportExportConsentError,
  clearReportExportConsentForUid,
} = reportExportConsentSlice.actions;

export default reportExportConsentSlice.reducer;
